import { ARENA_CX, ARENA_CY } from "@duelgame/shared";

export type Point = { x: number; y: number };

/**
 * Canvas-relative input: click-to-move / shoot, mouse aim tracking, emote key.
 */
export class Input {
  private canvas: HTMLCanvasElement;
  private enabled = false;
  mouse: Point = { x: ARENA_CX, y: ARENA_CY };
  /** Latest pointer in client (viewport) space — kept even while input is disabled. */
  private lastClient: Point | null = null;
  private clickCb: ((p: Point) => void) | null = null;
  private emoteCb: (() => void) | null = null;
  private abilityCb: (() => void) | null = null;
  private debugCb: (() => void) | null = null;

  private onTrack = (e: PointerEvent) => {
    this.lastClient = { x: e.clientX, y: e.clientY };
    if (this.enabled) {
      this.mouse = this.clientToCanvas(e.clientX, e.clientY);
    }
  };

  private onDown = (e: PointerEvent) => {
    if (!this.enabled) return;
    if (e.button !== 0) return;
    const p = this.clientToCanvas(e.clientX, e.clientY);
    this.lastClient = { x: e.clientX, y: e.clientY };
    this.mouse = p;
    this.clickCb?.(p);
  };

  private onKeyDown = (e: KeyboardEvent) => {
    if (!this.enabled) return;
    if (e.repeat) return;
    if (e.code === "KeyT" || e.key === "t" || e.key === "T") {
      e.preventDefault();
      this.emoteCb?.();
    } else if (e.code === "Space" || e.key === " ") {
      e.preventDefault();
      this.abilityCb?.();
    } else if (e.code === "KeyL" || e.key === "l" || e.key === "L") {
      e.preventDefault();
      this.debugCb?.();
    }
  };

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    // Track pointer even over UI / while disabled so enable() can sync.
    window.addEventListener("pointermove", this.onTrack);
  }

  onClick(cb: (p: Point) => void): void {
    this.clickCb = cb;
  }

  onEmote(cb: () => void): void {
    this.emoteCb = cb;
  }

  /** Active ability use (Space — e.g. Cowboy Dummy). */
  onAbilityUse(cb: () => void): void {
    this.abilityCb = cb;
  }

  /** Debug hotkeys (e.g. hitbox overlay). */
  onDebugToggle(cb: () => void): void {
    this.debugCb = cb;
  }

  enable(): void {
    if (!this.enabled) {
      this.enabled = true;
      this.canvas.addEventListener("pointerdown", this.onDown);
      window.addEventListener("keydown", this.onKeyDown);
    }
    // Catch up after UI click FX / overlay delays where moves were ignored for gameplay.
    this.syncMouseFromLastClient();
  }

  disable(): void {
    this.enabled = false;
    this.canvas.removeEventListener("pointerdown", this.onDown);
    window.removeEventListener("keydown", this.onKeyDown);
  }

  /** Push the latest tracked pointer into canvas mouse coords. */
  syncMouseFromLastClient(): void {
    if (!this.lastClient) return;
    this.mouse = this.clientToCanvas(this.lastClient.x, this.lastClient.y);
  }

  private clientToCanvas(clientX: number, clientY: number): Point {
    const rect = this.canvas.getBoundingClientRect();
    const sx = this.canvas.width / rect.width;
    const sy = this.canvas.height / rect.height;
    return {
      x: (clientX - rect.left) * sx,
      y: (clientY - rect.top) * sy,
    };
  }
}
